import { Node, Edge } from '../../../core/models/scene';

type CulturePalette = Record<string, string>;
type HealthPalette = Record<string, { color: string; icon?: string; propagate?: boolean }>;

export type ComputedCulture = Array<{ tag: string; weight: number; color: string }>;
export type ComputedHealth = Array<{ tag: string; color: string; icon?: string }>;

export interface ProjectionSettings {
  culturePalette: CulturePalette;
  healthPalette: HealthPalette;
}

export interface ProjectionResult {
  culture: Map<string, ComputedCulture>;
  health: Map<string, ComputedHealth>;
}

export const SUPPRESSED_INHERITED_CULTURE_KEY = 'suppressedInheritedCultureTags';
export const SUPPRESSED_INHERITED_HEALTH_KEY = 'suppressedInheritedHealthTags';

export const readSuppressedInheritedTags = (
  node: unknown,
  key: typeof SUPPRESSED_INHERITED_CULTURE_KEY | typeof SUPPRESSED_INHERITED_HEALTH_KEY,
): string[] => {
  const n = (node ?? {}) as Record<string, unknown>;
  const attrs = (n.attrs ?? {}) as Record<string, unknown>;
  const props = (n.props ?? {}) as Record<string, unknown>;
  const raw = Array.isArray(attrs[key]) ? attrs[key] : Array.isArray(props[key]) ? props[key] : n[key];
  return Array.isArray(raw) ? raw.filter((tag): tag is string => typeof tag === 'string') : [];
};

export class GenogramPerspectiveProjection {
  // Cache to avoid recomputation when inputs are effectively unchanged (e.g., panning)
  private static prevKey: string | null = null;
  private static prevResult: ProjectionResult | null = null;
  private static prevNodesRef: Node[] | null = null;
  private static prevEdgesRef: Edge[] | null = null;
  private static prevCultureRef: ProjectionSettings['culturePalette'] | null = null;
  private static prevHealthRef: ProjectionSettings['healthPalette'] | null = null;

  private static normalizePalette(palette: Record<string, any>): Array<[string, any]> {
    return Object.entries(palette || {}).sort(([a], [b]) => a.localeCompare(b));
  }

  private static buildSignature(nodes: Node[], edges: Edge[], settings: ProjectionSettings): string {
    const nodeSig = nodes
      .map((n) => {
        const attrs = (n as any).attrs || {};
        const props = (n as any).props || {};
        const culturalTags = Array.isArray(attrs.culturalHeritageTags)
          ? attrs.culturalHeritageTags
          : Array.isArray(props.culturalHeritageTags)
            ? props.culturalHeritageTags
          : Array.isArray((n as any).culturalHeritageTags)
            ? (n as any).culturalHeritageTags
            : [];
        const medicalTags = Array.isArray(attrs.medicalConditionTags)
          ? attrs.medicalConditionTags
          : Array.isArray(props.medicalConditionTags)
            ? props.medicalConditionTags
          : Array.isArray((n as any).medicalConditionTags)
            ? (n as any).medicalConditionTags
            : [];
        const override =
          attrs.overrideCultureInheritance ??
          props.overrideCultureInheritance ??
          (n as any).overrideCultureInheritance ??
          false;
        return [
          n.id,
          culturalTags,
          medicalTags,
          Boolean(override),
          readSuppressedInheritedTags(n, SUPPRESSED_INHERITED_CULTURE_KEY),
          readSuppressedInheritedTags(n, SUPPRESSED_INHERITED_HEALTH_KEY),
        ];
      })
      .sort((a, b) => (a[0] as string).localeCompare(b[0] as string));

    const edgeSig = edges
      .map((e) => {
        const attrs = (e.attrs as Record<string, unknown>) || {};
        const props = (e as any).props as Record<string, unknown> | undefined;
        const adopted = attrs.adopted ?? props?.adopted ?? (e as any).adopted;
        const guardianship = attrs.guardianship ?? props?.guardianship ?? (e as any).guardianship;
        const relation = attrs.relation ?? props?.relation ?? (e as any).relation;
        return [e.id, e.type, e.from, e.to, Boolean(adopted), Boolean(guardianship), relation];
      })
      .sort((a, b) => (a[0] as string).localeCompare(b[0] as string));

    const cultureSig = this.normalizePalette(settings.culturePalette);
    const healthSig = this.normalizePalette(settings.healthPalette);

    return JSON.stringify({ nodeSig, edgeSig, cultureSig, healthSig });
  }

  static compute(nodes: Node[], edges: Edge[], settings: ProjectionSettings): ProjectionResult {
    // Fast-path: if all input references are stable, reuse immediately without hashing
    if (
      this.prevResult &&
      this.prevNodesRef === nodes &&
      this.prevEdgesRef === edges &&
      this.prevCultureRef === settings.culturePalette &&
      this.prevHealthRef === settings.healthPalette
    ) {
      return this.prevResult;
    }

    const key = this.buildSignature(nodes, edges, settings);
    if (this.prevKey === key && this.prevResult) {
      return this.prevResult;
    }
    const childEdgeTypes = ['child', 'adoptive_child', 'foster_child'];

    const byId = new Map(nodes.map(n => [n.id, n] as const));
    const childrenOf = new Map<string, string[]>();
    const parentsOf = new Map<string, string[]>();
    const parentsOfForCulture = new Map<string, string[]>();
    const isNonGeneticParentChild = (e: Edge): boolean => {
      const t = (e.type as string)?.toLowerCase?.();
      if (t === 'adoption' || t === 'guardianship' || t === 'adoptive_child' || t === 'foster_child') return true;
      const attrs = (e.attrs as Record<string, unknown>) || {};
      const props = (e as any).props as Record<string, unknown> | undefined;
      const relation = (attrs.relation ?? props?.relation ?? (e as any).relation) as string | undefined;
      if (relation === 'adoptive_child' || relation === 'foster_child') return true;
      const a = attrs.adopted ?? props?.adopted ?? (e as any).adopted;
      const g = attrs.guardianship ?? props?.guardianship ?? (e as any).guardianship;
      if (a || g) return true;
      const dashed = attrs.dashed ?? props?.dashed ?? (e as any).dashed;
      const dash = attrs.dash ?? props?.dash ?? (e as any).dash;
      const dashMatch = Array.isArray(dash) && dash.length >= 2 && dash[0] === 9 && dash[1] === 9;
      if (dashed && dashMatch) return true;
      return false;
    };
    const marriageSpouses = new Map<string, [string, string]>();
    edges.filter(e => e.type === 'marriage').forEach(e => {
      marriageSpouses.set(e.id, [e.from, e.to]);
    });
    const childrenWithNonGeneticEdge = new Set<string>();
    edges.filter(e => childEdgeTypes.includes((e.type as string) || '')).forEach(e => {
      const childId = e.to;
      const sourceId = e.from;
      const parentIds = marriageSpouses.has(sourceId)
        ? marriageSpouses.get(sourceId)!
        : [sourceId];
      parentIds.forEach((parentId) => {
        if (!childrenOf.has(parentId)) childrenOf.set(parentId, []);
        childrenOf.get(parentId)!.push(childId);
        if (!parentsOf.has(childId)) parentsOf.set(childId, []);
        parentsOf.get(childId)!.push(parentId);
      });
      const isNonGenetic = isNonGeneticParentChild(e);
      if (isNonGenetic) childrenWithNonGeneticEdge.add(childId);
      if (!isNonGenetic) {
        parentIds.forEach((parentId) => {
          if (!parentsOfForCulture.has(childId)) parentsOfForCulture.set(childId, []);
          parentsOfForCulture.get(childId)!.push(parentId);
        });
      }
    });
    childrenWithNonGeneticEdge.forEach(childId => parentsOfForCulture.delete(childId));

    const dedupeParents = (m: Map<string, string[]>) => {
      m.forEach((plist, id) => m.set(id, Array.from(new Set(plist))));
    };
    dedupeParents(parentsOfForCulture);

    if (parentsOfForCulture.size > 0 && marriageSpouses.size > 0) {
      const spouseOf = new Map<string, Set<string>>();
      marriageSpouses.forEach(([a, b]) => {
        if (!spouseOf.has(a)) spouseOf.set(a, new Set());
        if (!spouseOf.has(b)) spouseOf.set(b, new Set());
        spouseOf.get(a)!.add(b);
        spouseOf.get(b)!.add(a);
      });
      parentsOfForCulture.forEach((plist, childId) => {
        if (plist.length === 1) {
          const p = plist[0];
          const spouses = Array.from(spouseOf.get(p) || []);
          if (spouses.length > 0) {
            plist.push(spouses[0]);
            parentsOfForCulture.set(childId, plist);
          }
        }
      });
    }

    if (parentsOf.size > 0 && marriageSpouses.size > 0) {
      // Build reverse spouse lookup: personId -> spouseIds
      const spouseOf = new Map<string, Set<string>>();
      marriageSpouses.forEach(([a, b]) => {
        if (!spouseOf.has(a)) spouseOf.set(a, new Set());
        if (!spouseOf.has(b)) spouseOf.set(b, new Set());
        spouseOf.get(a)!.add(b);
        spouseOf.get(b)!.add(a);
      });
      parentsOf.forEach((plist, childId) => {
        if (plist.length === 1) {
          const p = plist[0];
          const spouses = Array.from(spouseOf.get(p) || []);
          // If a spouse exists, include one (first) spouse as the second parent
          if (spouses.length > 0) {
            plist.push(spouses[0]);
            parentsOf.set(childId, plist);
          }
        }
      });
    }

    const culture = new Map<string, ComputedCulture>();
    const health = new Map<string, ComputedHealth>();
    const visitingCulture = new Set<string>(); // Track nodes currently being computed for culture to detect cycles
    const visitingHealth = new Set<string>(); // Track nodes currently being computed for health to detect cycles

    const getCultureMix = (id: string): ComputedCulture => {
      // If already computed, return cached result
      if (culture.has(id)) return culture.get(id)!;
      
      // Cycle detection: if this node is already being computed, return empty array to break the cycle
      if (visitingCulture.has(id)) {
        console.warn(`[GenogramPerspectiveProjection] Cycle detected in parent-child relationships for node ${id}, returning empty culture mix`);
        return [];
      }
      
      const node = byId.get(id);
      if (!node) return [];
      
      // Mark as visiting before recursing
      visitingCulture.add(id);
      
      try {
        const attrs = (node as any).attrs || {};
        const props = (node as any).props || {};
        const explicitFromAttrs = Array.isArray(attrs.culturalHeritageTags) ? (attrs.culturalHeritageTags as string[]) : [];
        const explicitFromProps = Array.isArray(props.culturalHeritageTags) ? (props.culturalHeritageTags as string[]) : [];
        const explicitFromTop = Array.isArray((node as any).culturalHeritageTags) ? ((node as any).culturalHeritageTags as string[]) : [];
        const explicit = explicitFromAttrs.length > 0 ? explicitFromAttrs : explicitFromProps.length > 0 ? explicitFromProps : explicitFromTop;

        const override = attrs.overrideCultureInheritance !== undefined
          ? Boolean(attrs.overrideCultureInheritance)
          : props.overrideCultureInheritance !== undefined
            ? Boolean(props.overrideCultureInheritance)
            : Boolean((node as any).overrideCultureInheritance);
        const mix = new Map<string, number>();
        // explicit tags weight 1 each before normalization
        explicit.forEach(t => mix.set(t, (mix.get(t) || 0) + 1));
        if (!override) {
          const suppressed = new Set(readSuppressedInheritedTags(node, SUPPRESSED_INHERITED_CULTURE_KEY));
          const parents = parentsOfForCulture.get(id) || [];
          if (parents.length > 0) {
            const weightPerParent = 1 / parents.length;
            parents.forEach(pid => {
              const pmix = getCultureMix(pid);
              pmix.forEach(({ tag, weight }) => {
                if (suppressed.has(tag)) return;
                mix.set(tag, (mix.get(tag) || 0) + weightPerParent * weight);
              });
            });
          }
        }
        // normalize
        const total = Array.from(mix.values()).reduce((a, b) => a + b, 0) || 0;
        const result = total > 0
          ? Array.from(mix.entries()).map(([tag, w]) => ({ tag, weight: w / total, color: settings.culturePalette[tag] || '#999' }))
          : [];
        // Cache the result before returning
        culture.set(id, result);
        return result;
      } finally {
        // Always remove from visiting set, even if an error occurs
        visitingCulture.delete(id);
      }
    };

    const fiftyFifty = (parentId: string, childId: string): boolean => {
      const key = `${parentId}:${childId}`;
      let hash = 0;
      for (let i = 0; i < key.length; i++) {
        hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
      }
      return (hash & 1) === 1;
    };

    const getHealthTags = (id: string): ComputedHealth => {
      // If already computed, return cached result
      if (health.has(id)) return health.get(id)!;
      
      // Cycle detection: if this node is already being computed, return empty array to break the cycle
      if (visitingHealth.has(id)) {
        console.warn(`[GenogramPerspectiveProjection] Cycle detected in parent-child relationships for node ${id}, returning empty health tags`);
        return [];
      }
      
      const node = byId.get(id);
      if (!node) return [];
      
      // Mark as visiting before recursing
      visitingHealth.add(id);
      
      try {
        const attrs = (node as any).attrs || {};
        const props = (node as any).props || {};
        const listFromAttrs = Array.isArray(attrs.medicalConditionTags) ? (attrs.medicalConditionTags as string[]) : [];
        const listFromProps = Array.isArray(props.medicalConditionTags) ? (props.medicalConditionTags as string[]) : [];
        const listFromTop = Array.isArray((node as any).medicalConditionTags) ? ((node as any).medicalConditionTags as string[]) : [];
        const list = listFromAttrs.length > 0 ? listFromAttrs : listFromProps.length > 0 ? listFromProps : listFromTop;
        const explicit = new Set<string>(list);
        const parents = parentsOfForCulture.get(id) || [];
        const parentInfos = parents.map(pid => ({
          id: pid,
          node: byId.get(pid),
          tags: getHealthTags(pid),
        }));

        const hasFatherHemA = parentInfos.some(p =>
          (p.node as any)?.sex === 'male' && p.tags.some(t => t.tag === 'hemophilia_a')
        );
        const hasMotherCarrier = parentInfos.some(p =>
          (p.node as any)?.sex === 'female' && p.tags.some(t => t.tag === 'hemophilia_a_carrier')
        );
        const hasMotherHemA = parentInfos.some(p =>
          (p.node as any)?.sex === 'female' && p.tags.some(t => t.tag === 'hemophilia_a')
        );

        parentInfos.forEach(({ id: pid, node: parentNode, tags: pList }) => {
          for (const item of pList) {
            const cfg = settings.healthPalette[item.tag];
            if (!cfg?.propagate) continue;
            const parentSex = (parentNode as any)?.sex;
            const childSex = (node as any)?.sex;
            const tag = item.tag;

            // Hemophilia propagation rules:
            if (tag === 'hemophilia_a') {
              if (parentSex === 'male' && childSex === 'female') {
                // Father affected
                if (hasMotherCarrier || hasMotherHemA) {
                  explicit.add('hemophilia_a'); // daughters can be affected
                } else {
                  explicit.add('hemophilia_a_carrier'); // daughters carriers when mother is not carrier/affected
                }
              } else if (parentSex === 'female') {
                explicit.add(tag); // affected mother passes condition
              }
              continue;
            }

            if (tag === 'hemophilia_a_carrier') {
              if (parentSex === 'female') {
                if (childSex === 'female') {
                  if (hasFatherHemA) {
                    explicit.add('hemophilia_a'); // father affected + mother carrier → daughters affected
                  } else {
                    explicit.add(tag); // daughters carriers
                  }
                } else if (childSex === 'male' && fiftyFifty(pid, id)) {
                  explicit.add('hemophilia_a'); // sons 50% chance affected
                }
              }
              // never propagate carrier to sons
              continue;
            }

            // Default propagation
            explicit.add(tag);
          }
        });
        readSuppressedInheritedTags(node, SUPPRESSED_INHERITED_HEALTH_KEY).forEach(tag => {
          if (!list.includes(tag)) explicit.delete(tag);
        });
        const result: ComputedHealth = Array.from(explicit).map(tag => ({ tag, color: settings.healthPalette[tag]?.color || '#999', icon: settings.healthPalette[tag]?.icon }));
        // Cache the result before returning
        health.set(id, result);
        return result;
      } finally {
        // Always remove from visiting set, even if an error occurs
        visitingHealth.delete(id);
      }
    };

    nodes.forEach(n => { getCultureMix(n.id); getHealthTags(n.id); });
    const result = { culture, health };
    this.prevKey = key;
    this.prevResult = result;
    this.prevNodesRef = nodes;
    this.prevEdgesRef = edges;
    this.prevCultureRef = settings.culturePalette;
    this.prevHealthRef = settings.healthPalette;
    return result;
  }
}
